import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { fmtINR, fmtNum, fmtDate } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Fuel, Upload, Plus, FileSpreadsheet, Loader2, Droplets, Flame, Truck, CreditCard, CalendarDays, ArrowRight, FileDown, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { printDayReport } from '@/lib/pumpPrint';

// Petroleum industry colour palette (user-specified)
const FUEL_COLORS = {
  ms: { bar: 'border-l-green-700', text: 'text-green-700', tint: 'bg-green-700/5' },        // forest green
  hsd: { bar: 'border-l-sky-500', text: 'text-sky-600', tint: 'bg-sky-500/5' },             // sky blue
  cng: { bar: 'border-l-lime-600', text: 'text-lime-600', tint: 'bg-lime-500/5' },          // lawn green
  gantry: { bar: 'border-l-sky-700', text: 'text-sky-700', tint: 'bg-sky-700/5' },          // HSD family
  bowser: { bar: 'border-l-sky-700', text: 'text-sky-700', tint: 'bg-sky-700/5' },          // HSD family
  lube: { bar: 'border-l-cyan-600', text: 'text-cyan-600', tint: 'bg-cyan-500/5' },         // water blue
  total: { bar: 'border-l-yellow-400', text: 'text-yellow-600', tint: 'bg-yellow-400/10' }, // yellow
  neutral: { bar: 'border-l-border', text: 'text-primary', tint: '' },
};

const KPI = ({ icon: Icon, label, litres, amount, testId, color = 'neutral' }) => {
  const c = FUEL_COLORS[color] || FUEL_COLORS.neutral;
  return (
    <div className={`rounded-xl border border-border border-l-4 ${c.bar} ${c.tint} bg-card p-4`} data-testid={testId}>
      <div className={`flex items-center gap-2 text-xs font-semibold ${c.text}`}><Icon className="h-4 w-4" />{label}</div>
      <p className="text-xl font-bold mt-1">{litres != null ? `${fmtNum(litres)} L` : fmtINR(amount)}</p>
      {litres != null && <p className="text-xs text-muted-foreground font-medium">{fmtINR(amount)}</p>}
    </div>
  );
};

const MODES = [
  { key: 'today', label: 'Today' },
  { key: 'day', label: 'Day' },
  { key: 'month', label: 'Month' },
  { key: 'year', label: 'Year' },
  { key: 'custom', label: 'Custom' },
  { key: 'all', label: 'All' },
];

const todayStr = () => new Date().toISOString().slice(0, 10);
const lastDayOfMonth = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
};

const DayStat = ({ no, label, value, rs, sub, subClass = 'text-muted-foreground', color = 'neutral', testId }) => {
  const c = FUEL_COLORS[color] || FUEL_COLORS.neutral;
  return (
    <div className={`rounded-lg border border-border border-l-4 ${c.bar} ${c.tint} bg-background p-3`} data-testid={testId}>
      <p className={`text-[11px] font-semibold leading-tight ${c.text}`}>{no != null && <span className="font-bold mr-1">{no}.</span>}{label}</p>
      <p className="text-sm font-bold mt-0.5">{value}</p>
      {rs && <p className="text-xs font-semibold text-foreground/80">{rs}</p>}
      {sub && <p className={`text-[11px] font-medium ${subClass}`}>{sub}</p>}
    </div>
  );
};

const DayDetailPanel = ({ ds }) => {
  if (!ds || !ds.exists) return null;
  const p = ds.prices, dip = ds.dip_stock, s = ds.sales, a = ds.amounts;
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3" data-testid="pump-day-detail">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold text-sm flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" />Day Detail — {fmtDate(ds.date)}{ds.operator_name ? <span className="text-xs text-muted-foreground font-normal">· Operator: {ds.operator_name}</span> : null}</p>
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge className="bg-green-700/10 text-green-700 border border-green-700/30 hover:bg-green-700/10" data-testid="day-ms-price">MS ₹{fmtNum(p.ms)}/L</Badge>
          <Badge className="bg-sky-500/10 text-sky-600 border border-sky-500/30 hover:bg-sky-500/10" data-testid="day-hsd-price">HSD ₹{fmtNum(p.hsd)}/L</Badge>
          <Badge className="bg-lime-500/10 text-lime-600 border border-lime-500/30 hover:bg-lime-500/10" data-testid="day-cng-price">CNG ₹{fmtNum(p.cng)}/kg</Badge>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2">
        <DayStat label="MS DIP STOCK" value={`${fmtNum(dip.ms_litres)} L`} sub={`Density: ${dip.ms_density ? fmtNum(dip.ms_density) : '—'}`} color="ms" testId="day-ms-dip" />
        <DayStat label="HSD DIP STOCK" value={`${fmtNum(dip.hsd_litres)} L`} sub={`Density: ${dip.hsd_density ? fmtNum(dip.hsd_density) : '—'}`} color="hsd" testId="day-hsd-dip" />
        <DayStat no={1} label="TOTAL MS SALE" value={`${fmtNum(s.ms_net_sale)} L`} rs={fmtINR(s.ms_amount)} sub={`Testing: ${fmtNum(s.ms_testing)} L`} subClass="text-red-600" color="ms" testId="day-ms-sale" />
        <DayStat no={2} label="TOTAL HSD SALE" value={`${fmtNum(s.hsd_net_sale)} L`} rs={fmtINR(s.hsd_amount)} sub={`Testing: ${fmtNum(s.hsd_testing)} L`} subClass="text-red-600" color="hsd" testId="day-hsd-sale" />
        <DayStat no={3} label="TOTAL CNG SALE" value={`${fmtNum(s.cng_net_sale)} kg`} rs={fmtINR(s.cng_amount)} color="cng" testId="day-cng-sale" />
        <DayStat no={4} label="TOTAL GANTRY SALE" value={`${fmtNum(s.gantry_net_sale)} L`} rs={fmtINR(s.gantry_amount)} color="gantry" testId="day-gantry-sale" />
        <DayStat no={5} label="TOTAL BOWSER SALE" value={`${fmtNum(s.bowser_sale)} L`} rs={fmtINR(s.bowser_amount)} color="bowser" testId="day-bowser-sale" />
        <DayStat no={6} label="TOTAL LUBE SALE" value={fmtINR(s.lube_amount)} color="lube" testId="day-lube-sale" />
        <DayStat no={7} label="TOTAL SALE" value={fmtINR(a.total_sale_rs)} color="total" testId="day-total-sale" />
        <DayStat no={8} label="TOTAL CREDIT SALE" value={fmtINR(a.credit_sale_rs)} testId="day-credit-sale" />
        <DayStat no={9} label="TOTAL CASH SALE" value={fmtINR(a.cash_sale_rs)} testId="day-cash-sale" />
        <DayStat no={10} label="CASH DEPOSIT IN BANK" value={fmtINR(a.cash_deposit_bank_rs)} testId="day-cash-deposit" />
      </div>
    </div>
  );
};

export default function PumpDashboard() {
  const nav = useNavigate();
  const loc = useLocation();
  const base = loc.pathname.startsWith('/admin') ? '/admin/pump' : '/pump';
  const today = todayStr();
  const [dash, setDash] = useState(null);
  const [reports, setReports] = useState(null);
  const [daySum, setDaySum] = useState(null);
  const [mode, setMode] = useState('today');
  const [day, setDay] = useState(today);
  const [month, setMonth] = useState(today.slice(0, 7));
  const [year, setYear] = useState(today.slice(0, 4));
  const [range, setRange] = useState({ from: '', to: '' });
  const [importing, setImporting] = useState(false);
  const [newDate, setNewDate] = useState(today);
  const fileRef = useRef(null);

  const params = useMemo(() => {
    if (mode === 'today') return { from: today, to: today };
    if (mode === 'day') return { from: day, to: day };
    if (mode === 'month') return { from: `${month}-01`, to: lastDayOfMonth(month) };
    if (mode === 'year') return { from: `${year}-01-01`, to: `${year}-12-31` };
    if (mode === 'custom') {
      const p = {};
      if (range.from) p.from = range.from;
      if (range.to) p.to = range.to;
      return p;
    }
    return {};
  }, [mode, day, month, year, range, today]);

  const load = useCallback(() => {
    api.get('/pump/dashboard', { params }).then((r) => setDash(r.data)).catch(() => toast.error('Could not load dashboard data'));
    api.get('/pump/reports', { params }).then((r) => setReports(r.data)).catch(() => setReports([]));
    let singleDay = null;
    if (mode === 'today') singleDay = today;
    else if (mode === 'day') singleDay = day;
    if (singleDay) api.get(`/pump/day-summary/${singleDay}`).then((r) => setDaySum(r.data)).catch(() => setDaySum(null));
    else setDaySum(null);
  }, [params, mode, day, today]);
  useEffect(() => { load(); }, [load]);

  const yearOptions = useMemo(() => {
    const cur = Number(today.slice(0, 4));
    const opts = [];
    for (let y = cur; y >= cur - 4; y--) opts.push(String(y));
    return opts;
  }, [today]);

  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadFile = async (path, filename, mime) => {
    setPdfBusy(true);
    try {
      const r = await api.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`${filename} downloaded`);
    } catch (e) {
      toast.error(e.response?.status === 404 ? 'No data available for the selected period' : 'Could not download file');
    }
    setPdfBusy(false);
  };
  const downloadPdf = (path, filename) => downloadFile(path, filename, 'application/pdf');
  const downloadXls = (path, filename) => downloadFile(path, filename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

  // One-tap A4 print of the full day report (no popup windows).
  const printDay = async () => {
    const d = mode === 'today' ? today : day;
    try {
      const r = await api.get(`/pump/reports/${d}`);
      printDayReport(r.data);
    } catch (e) {
      toast.error(e.response?.status === 404 ? 'No report for this date' : 'Could not open the print view');
    }
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setImporting(true);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await api.post('/pump/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Imported ${r.data.imported} days (${r.data.date_from} → ${r.data.date_to})`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
    setImporting(false);
  };

  const scopeLabel = useMemo(() => {
    if (!dash) return 'Loading…';
    if (mode === 'today') return `Today · ${fmtDate(today)}`;
    if (mode === 'day') return `Day · ${fmtDate(day)}`;
    if (mode === 'month') return `Month · ${month}`;
    if (mode === 'year') return `Year · ${year}`;
    if (mode === 'all') return `All data · ${dash.days} days`;
    return `${dash.days} days · ${fmtDate(dash.date_from)} → ${fmtDate(dash.date_to)}`;
  }, [dash, mode, day, month, year, today]);

  const t = dash?.totals || {};
  const maxSettle = Math.max(...(dash?.settlement_mix || []).map((s) => s.amount), 1);
  const noDataToday = mode === 'today' && dash && dash.days === 0;

  // Month (YYYY-MM) targeted by the Month PDF/Excel buttons for the current view
  const selectedMonth = () => {
    if (mode === 'month') return month;
    if (mode === 'day') return day.slice(0, 7);
    return today.slice(0, 7);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Fuel className="h-5 w-5 text-primary" />Petrol Pump — Daily Sales</h2>
          <p className="text-sm text-muted-foreground" data-testid="pump-scope-label">{scopeLabel}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {(mode === 'today' || mode === 'day') && daySum?.exists && (
            <>
              <Button variant="secondary" className="h-9" onClick={printDay} data-testid="pump-day-print-button">
                <Printer className="h-4 w-4 mr-1.5" />Print
              </Button>
              <Button variant="secondary" className="h-9" disabled={pdfBusy}
                onClick={() => { const d = mode === 'today' ? today : day; downloadPdf(`/pump/reports/${d}/pdf`, `pump-day-${d}.pdf`); }}
                data-testid="pump-day-pdf-button">
                {pdfBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />}Day PDF
              </Button>
              <Button variant="secondary" className="h-9" disabled={pdfBusy}
                onClick={() => { const d = mode === 'today' ? today : day; downloadXls(`/pump/reports/${d}/excel`, `pump-day-${d}.xlsx`); }}
                data-testid="pump-day-excel-button">
                {pdfBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />}Day Excel
              </Button>
            </>
          )}
          <Button variant="secondary" className="h-9" disabled={pdfBusy}
            onClick={() => { const m = selectedMonth(); downloadPdf(`/pump/monthly-pdf?month=${m}`, `pump-month-${m}.pdf`); }}
            data-testid="pump-month-pdf-button">
            {pdfBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />}Month PDF
          </Button>
          <Button variant="secondary" className="h-9" disabled={pdfBusy}
            onClick={() => { const m = selectedMonth(); downloadXls(`/pump/monthly-excel?month=${m}`, `pump-month-${m}.xlsx`); }}
            data-testid="pump-month-excel-button">
            {pdfBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />}Month Excel
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={onImport} data-testid="pump-import-input" />
          <Button variant="secondary" className="h-9" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="pump-import-button">
            {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Import Excel
          </Button>
          <div className="flex items-end gap-1">
            <div><label className="text-xs text-muted-foreground">New report</label><Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="h-9" data-testid="pump-new-date" /></div>
            <Button className="h-9" onClick={() => newDate && nav(`${base}/report/${newDate}`)} data-testid="pump-new-report-button"><Plus className="h-4 w-4 mr-1" />Entry</Button>
          </div>
        </div>
      </div>

      {/* Date scope selector */}
      <div className="rounded-xl border border-border bg-card p-3 flex flex-wrap items-end gap-3" data-testid="pump-scope-bar">
        <div className="flex items-center rounded-lg border border-border bg-muted/40 p-0.5" role="tablist">
          {MODES.map((m) => (
            <button key={m.key} role="tab" aria-selected={mode === m.key}
              onClick={() => setMode(m.key)}
              className={`px-3 h-8 rounded-md text-xs font-semibold transition-colors ${mode === m.key ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid={`pump-mode-${m.key}`}>
              {m.label}
            </button>
          ))}
        </div>
        {mode === 'day' && (
          <div><label className="text-xs text-muted-foreground">Pick date</label><Input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className="h-9 w-40" data-testid="pump-pick-day" /></div>
        )}
        {mode === 'month' && (
          <div><label className="text-xs text-muted-foreground">Pick month</label><Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="h-9 w-44" data-testid="pump-pick-month" /></div>
        )}
        {mode === 'year' && (
          <div>
            <label className="text-xs text-muted-foreground">Pick year</label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="h-9 w-32" data-testid="pump-pick-year"><SelectValue /></SelectTrigger>
              <SelectContent>{yearOptions.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {mode === 'custom' && (
          <>
            <div><label className="text-xs text-muted-foreground">From</label><Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="h-9 w-40" data-testid="pump-filter-from" /></div>
            <div><label className="text-xs text-muted-foreground">To</label><Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="h-9 w-40" data-testid="pump-filter-to" /></div>
          </>
        )}
      </div>

      {/* Empty state for today */}
      {noDataToday && (
        <div className="rounded-xl border border-dashed border-border bg-muted/30 p-5 flex flex-wrap items-center justify-between gap-3" data-testid="pump-no-today-banner">
          <div className="flex items-center gap-3">
            <CalendarDays className="h-8 w-8 text-primary" />
            <div>
              <p className="font-semibold text-sm">No report for today ({fmtDate(today)}) yet</p>
              <p className="text-xs text-muted-foreground">Create today's entry, or jump to the latest available day{dash?.latest_date ? ` (${fmtDate(dash.latest_date)})` : ''}.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => nav(`${base}/report/${today}`)} data-testid="pump-create-today-button"><Plus className="h-4 w-4 mr-1" />Create today's report</Button>
            {dash?.latest_date && (
              <Button size="sm" variant="secondary" onClick={() => { setDay(dash.latest_date); setMode('day'); }} data-testid="pump-jump-latest-button">
                Latest day<ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Single-day detail panel (replaces KPI boxes for day views) */}
      <DayDetailPanel ds={daySum} />

      {/* KPI grid: hidden for single-day views; skeletons while loading */}
      {!daySum?.exists && !dash && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {['ms', 'hsd', 'cng', 'gantry', 'lube', 'total'].map((k) => <Skeleton key={`kpi-skel-${k}`} className="h-24" />)}
        </div>
      )}
      {!daySum?.exists && dash && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <KPI icon={Fuel} label="MS (Petrol)" litres={t.ms_net_sale} amount={t.ms_amount} color="ms" testId="kpi-ms" />
          <KPI icon={Droplets} label="HSD (Diesel)" litres={t.hsd_net_sale} amount={t.hsd_amount} color="hsd" testId="kpi-hsd" />
          <KPI icon={Flame} label="CNG" litres={t.cng_net_sale} amount={t.cng_amount} color="cng" testId="kpi-cng" />
          <KPI icon={Truck} label="Gantry + Bowser" litres={(t.gantry_net_sale || 0) + (t.bowser_sale || 0)} amount={(t.gantry_amount || 0) + (t.bowser_amount || 0)} color="gantry" testId="kpi-gantry" />
          <KPI icon={FileSpreadsheet} label="Lubes Revenue" amount={t.lube_total_amount} color="lube" testId="kpi-lubes" />
          <KPI icon={CreditCard} label="Credit Sales" amount={t.total_credit_sale} color="total" testId="kpi-credit" />
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <span className="font-semibold text-sm">Daily Reports</span>
            <span className="text-xs text-muted-foreground">{reports?.length ?? '…'} days</span>
          </div>
          <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur text-xs text-muted-foreground">
                <tr>{['Date', 'MS (L)', 'HSD (L)', 'CNG (L)', 'Lubes ₹', 'Credit ₹', 'Source', ''].map((h) => <th key={h} className="text-left px-3 py-2 font-medium whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody>
                {(reports || []).map((r) => (
                  <tr key={r.date} className="border-t border-border hover:bg-muted/50 cursor-pointer" onClick={() => nav(`${base}/report/${r.date}`)} data-testid={`pump-report-row-${r.date}`}>
                    <td className="px-3 py-2 font-medium whitespace-nowrap">{fmtDate(r.date)}</td>
                    <td className="px-3 py-2">{fmtNum(r.ms_net_sale)}</td>
                    <td className="px-3 py-2">{fmtNum(r.hsd_net_sale)}</td>
                    <td className="px-3 py-2">{fmtNum(r.cng_net_sale)}</td>
                    <td className="px-3 py-2">{fmtINR(r.lube_total_amount)}</td>
                    <td className="px-3 py-2">{fmtINR(r.total_credit_sale)}</td>
                    <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{r.edited ? 'edited' : r.source}</Badge></td>
                    <td className="px-3 py-2 text-primary text-xs font-medium">Open</td>
                  </tr>
                ))}
                {reports && reports.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground text-sm">No reports in this period — pick another date, import an Excel file or create a new entry.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <p className="font-semibold text-sm mb-3">Settlement Mix (payment modes)</p>
          <div className="space-y-2.5" data-testid="settlement-mix">
            {(dash?.settlement_mix || []).slice(0, 10).map((s) => (
              <div key={s.mode}>
                <div className="flex justify-between text-xs mb-0.5"><span className="font-medium">{s.mode}</span><span className="text-muted-foreground">{fmtINR(s.amount)}</span></div>
                <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-primary" style={{ width: `${Math.max((s.amount / maxSettle) * 100, 2)}%` }} /></div>
              </div>
            ))}
            {dash && dash.settlement_mix.length === 0 && <p className="text-xs text-muted-foreground">No settlement data in range.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
